/**
 * Calendar Component (shadcn/ui pattern)
 *
 * Built on react-day-picker v9 with pt-BR locale by default.
 * Supports single date, range, and multiple selection modes.
 *
 * @module components/ui
 */

import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { ptBR } from 'date-fns/locale/pt-BR';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CalendarProps = DayPickerProps;

function Calendar({ className, classNames, ...props }: CalendarProps) {
  return (
    <DayPicker
      locale={ptBR}
      showOutsideDays
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        month_caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-foreground',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          'absolute left-1 top-0 inline-flex items-center justify-center rounded-lg',
          'h-7 w-7 bg-transparent p-0 text-muted-foreground opacity-50',
          'hover:opacity-100 hover:bg-accent hover:text-accent-foreground'
        ),
        button_next: cn(
          'absolute right-1 top-0 inline-flex items-center justify-center rounded-lg',
          'h-7 w-7 bg-transparent p-0 text-muted-foreground opacity-50',
          'hover:opacity-100 hover:bg-accent hover:text-accent-foreground'
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-lg w-9 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          'relative p-0 text-center text-sm',
          'focus-within:relative focus-within:z-20',
          '[&:has([aria-selected])]:bg-accent',
          '[&:has([aria-selected].day-outside)]:bg-accent/50',
          '[&:has([aria-selected].day-range-end)]:rounded-r-md'
        ),
        day_button: cn(
          'inline-flex items-center justify-center rounded-lg',
          'h-9 w-9 p-0 font-normal',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'aria-selected:opacity-100'
        ),
        range_end: 'day-range-end',
        selected: cn(
          'bg-primary text-primary-foreground',
          'hover:bg-primary hover:text-primary-foreground',
          'focus:bg-primary focus:text-primary-foreground'
        ),
        today: 'bg-accent text-accent-foreground',
        outside: cn(
          'day-outside text-muted-foreground opacity-50',
          'aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30'
        ),
        disabled: 'text-muted-foreground opacity-50',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
